import React from 'react';
import { DEFAULT_MEMBERSHIP_TYPE_OPTIONS, DEFAULT_PLAYER_TYPE_OPTIONS } from '../constants/membership';
import {
  normalizeMembershipFormConfig,
  isNonMemberGuestMembershipType,
  getMembershipStatusLabel,
  getMembershipRenewalStatus,
  isLifeMembershipType,
} from '../utils/membership';
import { API } from '../api';
import { useSiteMembershipDefaults } from '../utils/siteDefaults';
import { formatOrdinalDate, fmtLabel, toDateInputValue } from '../utils/format';
import TrophySvg from './TrophySvg';
import ClubEmailsCard from './ClubEmailsCard';
import MemberRecordCard from './MemberRecordCard';

function PlayerProfileContent({ clubs, confirmMembershipForProfile, currentUser, deletePlayer, editingProfile, getMembershipRouteRef, isAdmin, matches, memberQrData, openComp, openTeamProfile, playerProfile, profileForm, resetPlayerPassword, resetPwOpen, resetPwValue, saveProfile, setEditingProfile, setMembershipRoute, setPlayerProfile, setProfileForm, setResetPwOpen, setResetPwValue, showToast, teams, users }) {
  /* Before the early return: a hook cannot sit behind a condition. */
  const siteMembershipDefaults = useSiteMembershipDefaults();
  if (!playerProfile) return null;
  const bowlingStartYear = playerProfile.bowlingStartYear ? parseInt(playerProfile.bowlingStartYear, 10) : null;
  const currentYear = new Date().getFullYear();
  const yearsPlaying = bowlingStartYear && bowlingStartYear <= currentYear ? (currentYear - bowlingStartYear + 1) : null;
  const canViewPersonalProfileFields = !!(isAdmin || (currentUser && playerProfile.id && currentUser.id === playerProfile.id));
  const profileClubId = profileForm.ClubId || playerProfile.ClubId || (currentUser ? currentUser.ClubId : null);
  const profileClub = clubs.find(c => String(c.id) === String(profileClubId));
  const profileMembershipConfig = normalizeMembershipFormConfig(profileClub && profileClub.membershipFormConfig);
  const profileMembershipTypeOptions = (profileClub && Array.isArray(profileClub.membershipTypeOptions) && profileClub.membershipTypeOptions.length)
    ? profileClub.membershipTypeOptions
    : DEFAULT_MEMBERSHIP_TYPE_OPTIONS;
  const filteredProfileMembershipTypeOptions = profileMembershipTypeOptions.filter(opt => !isNonMemberGuestMembershipType(opt));
  const profilePlayerTypeOptions = (profileClub && Array.isArray(profileClub.playerTypeOptions) && profileClub.playerTypeOptions.length)
    ? profileClub.playerTypeOptions
    : DEFAULT_PLAYER_TYPE_OPTIONS;
  const profilePlatformMembershipFields = (profileMembershipConfig.fields || [])
    .filter(field => field && field.enabled && field.storage === 'platform')
    .filter(field => field.key !== 'fullName' && field.key !== 'email' && field.key !== 'yearsPlaying');
  const profileExtraData = (profileForm.membershipExtraData && typeof profileForm.membershipExtraData === 'object' && !Array.isArray(profileForm.membershipExtraData))
    ? profileForm.membershipExtraData
    : {};
  const profileDisplayExtraData = (playerProfile.membershipExtraData && typeof playerProfile.membershipExtraData === 'object' && !Array.isArray(playerProfile.membershipExtraData))
    ? playerProfile.membershipExtraData
    : {};
  const membershipStatusLabel = getMembershipStatusLabel(playerProfile);
  const membershipApprovalStatus = membershipStatusLabel === 'Pending' ? 'pending' : 'approved';
  const membershipPendingIntent = String(playerProfile.membershipPendingIntent || '').toLowerCase() === 'renew' ? 'renew' : 'apply';
  const isGuestMembershipType = isNonMemberGuestMembershipType(playerProfile.membershipType);
  const renewalStatus = getMembershipRenewalStatus(playerProfile);
  const renewalDueText = renewalStatus.date ? formatOrdinalDate(renewalStatus.date) : 'Not set';
  const canConfirmMembership = !!(currentUser && currentUser.role === 'club-admin');

  /* The server sends the stored record to admins only, and openProfile copies
     it into the form; its absence is what keeps this off a new player. */
  const hasMembershipRecord = !!(isAdmin && playerProfile.id && profileForm.membershipStanding);
  const editingLifeMember = isLifeMembershipType(profileForm.membershipType);
  const recordDateFields = [
    { key: 'membershipAppliedAt', label: 'Form received' },
    {
      key: 'membershipApprovedAt', label: 'Approved on',
      hidden: profileForm.membershipStanding !== 'approved',
    },
    { key: 'membershipFeePaidAt', label: 'Subs paid on' },
    { key: 'membershipCardIssuedAt', label: 'Card issued on' },
    {
      key: 'membershipRenewalDueAt', label: 'Renewal due',
      disabled: editingLifeMember,
      help: editingLifeMember ? 'Life members do not renew.' : 'Blank falls back to the club’s renewal day, if one is set.',
    },
  ];

  /* Approving fills in today so the date is never silently missing; going
     back to "no form on file" clears it, because an approval date is what
     the rest of the app reads as "is a member". */
  /* Sending somebody back for approval is treated as a new form coming in:
     subs and card start again, so once approved they show on New members.
     Cleared here rather than only on the server so the admin sees it, and
     can put a date back if that is really what they mean. */
  const setStanding = (standing) => {
    setProfileForm(f => ({
      ...f,
      membershipStanding: standing,
      membershipApprovedAt: standing === 'approved'
        ? (f.membershipApprovedAt || toDateInputValue(new Date()))
        : (standing === 'none' ? '' : f.membershipApprovedAt),
      ...(standing === 'pending' && playerProfile.membershipApprovalStatus !== 'pending'
        ? { membershipFeePaidAt: '', membershipCardIssuedAt: '' }
        : {}),
    }));
  };

  const valueForProfileMembershipField = (field) => {
    if (field.key === 'phone') return profileForm.phone || '';
    if (field.key === 'membershipType') return profileForm.membershipType || '';
    if (field.key === 'playerType') return profileForm.playerType || '';
    /* A column on the user, like the two above it, not one of the extras. */
    if (field.key === 'ageGroup') return profileForm.ageGroup || '';
    const raw = profileExtraData[field.key];
    if (field.type === 'boolean') return !!raw;
    return raw || '';
  };

  const updateProfileMembershipField = (field, rawValue) => {
    if (field.key === 'membershipType' && isGuestMembershipType) {
      return;
    }
    if (field.key === 'phone' || field.key === 'membershipType' || field.key === 'playerType' || field.key === 'ageGroup') {
      setProfileForm(prev => ({ ...prev, [field.key]: rawValue }));
      return;
    }
    setProfileForm(prev => {
      const nextExtra = {
        ...(prev.membershipExtraData && typeof prev.membershipExtraData === 'object' && !Array.isArray(prev.membershipExtraData)
          ? prev.membershipExtraData
          : {}),
        [field.key]: field.type === 'boolean' ? !!rawValue : rawValue,
      };
      return { ...prev, membershipExtraData: nextExtra };
    });
  };

  const optionsForProfileMembershipField = (field) => {
    if (field.optionsSource === 'membershipTypeOptions') return filteredProfileMembershipTypeOptions;
    if (field.optionsSource === 'playerTypeOptions') return profilePlayerTypeOptions;
    if (field.optionsSource === 'ageGroupOptions') return siteMembershipDefaults.ageGroupOptions;
    return Array.isArray(field.options) ? field.options : [];
  };

  return (<>
    {/* Profile Header */}
    <div className="w3-card w3-round w3-white">
      <div className="w3-container w3-theme-d3 w3-round-top profile-head">
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2 style={{ margin: 0, color: '#fff' }}>{playerProfile.id ? playerProfile.name : 'New Player'}</h2>
          <div style={{ color: '#b0bec5', marginTop: '4px', fontSize: 'var(--gs-text-sm)' }}>
            {playerProfile.id
              ? <>Player #{playerProfile.id} &bull; Member since {new Date(playerProfile.createdAt).toLocaleDateString()}</>
              : 'Fill in the details below to create a new player'}
          </div>
          {playerProfile.id && (
            <div className="profile-pills">
              <span className={`w3-tag w3-round ${membershipApprovalStatus === 'pending' ? 'w3-amber' : membershipStatusLabel === 'No Membership Application' ? 'w3-theme-l4' : membershipPendingIntent === 'renew' || membershipStatusLabel === 'Renewal Due' ? 'w3-orange' : 'w3-green'}`}>
                {membershipApprovalStatus === 'pending'
                  ? `${membershipPendingIntent === 'renew' ? 'Renewal' : 'Membership'} Pending Approval`
                  : membershipStatusLabel}
              </span>
              {canViewPersonalProfileFields && (
                <span className="w3-tag w3-round w3-theme-l4 profile-pill-renewal">
                  <span>{renewalStatus.isLife ? 'Membership:' : (renewalStatus.isExpired ? 'Renewal due:' : 'Membership expires:')}</span>
                  <strong>{renewalStatus.isLife ? 'Never expires' : renewalDueText}</strong>
                  {renewalStatus.isExpired && (
                    <button
                      className="w3-button w3-tiny w3-round w3-white w3-text-theme profile-pill-renew"
                      onClick={() => {
                        const clubRef = getMembershipRouteRef(currentUser.ClubId);
                        window.history.pushState({}, '', `/membership/${clubRef}/renew`);
                        setMembershipRoute({ clubRef: decodeURIComponent(clubRef), intent: 'renew' });
                      }}>
                      <i className="fa fa-refresh w3-margin-right"></i>Renew
                    </button>
                  )}
                </span>
              )}
            </div>
          )}
          {playerProfile.id && bowlingStartYear && (
            <div style={{ color: '#e1f5fe', marginTop: '6px', fontSize: 'var(--gs-text-sm)' }}>
              Bowling since {bowlingStartYear} &bull; {yearsPlaying !== null ? `${yearsPlaying} year${yearsPlaying === 1 ? '' : 's'} playing` : 'Years of play unavailable'}
            </div>
          )}
        </div>
        {isAdmin && playerProfile.id && (
          <div className="profile-head-actions">
            {canConfirmMembership && membershipApprovalStatus === 'pending' && (
              <button className="w3-button w3-round w3-green" onClick={confirmMembershipForProfile}>
                <i className="fa fa-check w3-margin-right"></i>{membershipPendingIntent === 'renew' ? 'Confirm Renewal' : 'Confirm Membership'}
              </button>
            )}
            <button className="w3-button w3-round w3-theme-l2"
              onClick={() => setEditingProfile(!editingProfile)}>
              <i className={`fa ${editingProfile ? 'fa-times' : 'fa-pencil'} w3-margin-right`}></i>
              {editingProfile ? 'Cancel' : 'Edit'}
            </button>
          </div>
        )}
      </div>
      {editingProfile && (
        <div className="w3-container w3-padding w3-pale-blue w3-animate-opacity">
          <div className="w3-row-padding" style={{ margin: '0 -8px' }}>
            <div className="w3-col m6 s12">
              <label className="w3-small w3-text-grey">Full Name</label>
              <input className="w3-input w3-border w3-round w3-margin-bottom" value={profileForm.name}
                onChange={e => setProfileForm(f => ({ ...f, name: e.target.value }))} />
            </div>
            <div className="w3-col m6 s12">
              <label className="w3-small w3-text-grey">Nickname</label>
              <input className="w3-input w3-border w3-round w3-margin-bottom" placeholder="Optional"
                value={profileForm.nickname || ''}
                onChange={e => setProfileForm(f => ({ ...f, nickname: e.target.value }))} />
            </div>
            <div className="w3-col m6 s12">
              <label className="w3-small w3-text-grey">Bowling Start Year</label>
              <input className="w3-input w3-border w3-round w3-margin-bottom" type="number" min="1900" max={new Date().getFullYear()}
                placeholder="e.g. 2012"
                value={profileForm.bowlingStartYear || ''}
                onChange={e => setProfileForm(f => ({ ...f, bowlingStartYear: e.target.value ? parseInt(e.target.value, 10) : '' }))} />
              {profileForm.bowlingStartYear ? (() => {
                const startYear = parseInt(profileForm.bowlingStartYear, 10);
                const years = startYear <= new Date().getFullYear() ? (new Date().getFullYear() - startYear + 1) : null;
                return years !== null ? (
                  <div className="w3-small w3-text-grey">Auto-calculated: {years} year{years === 1 ? '' : 's'} playing</div>
                ) : null;
              })() : null}
            </div>
            <div className="w3-col m6 s12">
              <label className="w3-small w3-text-grey">Email</label>
              <input className="w3-input w3-border w3-round w3-margin-bottom" type="email" placeholder="Optional"
                value={profileForm.email}
                onChange={e => setProfileForm(f => ({ ...f, email: e.target.value }))} />
            </div>
            {clubs.length > 0 && (
              <div className="w3-col m6 s12">
                <label className="w3-small w3-text-grey">Club</label>
                <select className="w3-select w3-border w3-round w3-margin-bottom"
                  value={profileForm.ClubId || ''}
                  onChange={e => setProfileForm(f => ({ ...f, ClubId: e.target.value ? parseInt(e.target.value) : null }))}>
                  <option value="">— No Club —</option>
                  {clubs.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            )}

            {profilePlatformMembershipFields.map(field => {
              const value = valueForProfileMembershipField(field);
              const options = optionsForProfileMembershipField(field);
              const fieldReadOnly = field.key === 'membershipType' && isGuestMembershipType;
              return (
                <div key={`profile-membership-${field.key}`} className="w3-col m6 s12">
                  <label className="w3-small w3-text-grey">{field.label}{field.required ? ' *' : ''}</label>
                  {field.type === 'select' ? (
                    <select
                      className="w3-select w3-border w3-round w3-margin-bottom"
                      value={value}
                      required={!!field.required}
                      disabled={fieldReadOnly}
                      onChange={e => updateProfileMembershipField(field, e.target.value)}>
                      <option value="">Select...</option>
                      {options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  ) : field.type === 'boolean' ? (
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', marginTop: '6px', marginBottom: '12px' }}>
                      <input
                        type="checkbox"
                        checked={!!value}
                        onChange={e => updateProfileMembershipField(field, e.target.checked)}
                      />
                      <span className="w3-small">Yes</span>
                    </label>
                  ) : field.type === 'number' ? (
                    <input
                      className="w3-input w3-border w3-round w3-margin-bottom"
                      type="number"
                      value={value}
                      required={!!field.required}
                      onChange={e => updateProfileMembershipField(field, e.target.value)}
                    />
                  ) : field.type === 'date' ? (
                    <input
                      className="w3-input w3-border w3-round w3-margin-bottom"
                      type="date"
                      value={value}
                      required={!!field.required}
                      onChange={e => updateProfileMembershipField(field, e.target.value)}
                    />
                  ) : (
                    <input
                      className="w3-input w3-border w3-round w3-margin-bottom"
                      type="text"
                      value={value}
                      required={!!field.required}
                      onChange={e => updateProfileMembershipField(field, e.target.value)}
                    />
                  )}
                </div>
              );
            })}
            {/* The club's form may not ask for a phone number, but the
                admin still needs somewhere to write one down. */}
            {hasMembershipRecord && !profilePlatformMembershipFields.some(field => field.key === 'phone') && (
              <div className="w3-col m6 s12">
                <label className="w3-small w3-text-grey">Phone</label>
                <input className="w3-input w3-border w3-round w3-margin-bottom" type="tel" placeholder="Optional"
                  value={profileForm.phone || ''}
                  onChange={e => setProfileForm(f => ({ ...f, phone: e.target.value }))} />
              </div>
            )}
          </div>
          {hasMembershipRecord && (
            <div style={{ borderTop: '1px solid var(--gs-border, #ddd)', paddingTop: '12px', marginBottom: '12px' }}>
              <strong>Membership record</strong>
              <p className="w3-small w3-text-grey" style={{ margin: '4px 0 12px' }}>
                For members who joined before the online form, or whose record is wrong. Leave a date blank
                if it has not happened yet.
              </p>
              <div className="w3-row-padding" style={{ margin: '0 -8px' }}>
                <div className="w3-col m6 s12">
                  <label className="w3-small w3-text-grey" htmlFor="profile-standing">Membership status</label>
                  <select id="profile-standing" className="w3-select w3-border w3-round w3-margin-bottom"
                    value={profileForm.membershipStanding}
                    onChange={e => setStanding(e.target.value)}>
                    <option value="none">No membership form on file</option>
                    <option value="pending">Waiting for approval</option>
                    <option value="approved">Approved member</option>
                  </select>
                </div>
                <div className="w3-col m6 s12">
                  <label className="w3-small w3-text-grey" htmlFor="profile-number">Membership number</label>
                  <input id="profile-number" className="w3-input w3-border w3-round w3-margin-bottom"
                    placeholder="Blank issues the next number"
                    value={profileForm.membershipNumber || ''}
                    onChange={e => setProfileForm(f => ({ ...f, membershipNumber: e.target.value }))} />
                </div>
                {recordDateFields.map(({ key, label, hidden, disabled, help }) => (hidden ? null : (
                  <div key={key} className="w3-col m6 s12">
                    <label className="w3-small w3-text-grey" htmlFor={`profile-${key}`}>{label}</label>
                    <input id={`profile-${key}`} className="w3-input w3-border w3-round" type="date"
                      value={profileForm[key] || ''}
                      disabled={disabled}
                      onChange={e => setProfileForm(f => ({ ...f, [key]: e.target.value }))} />
                    <div className="w3-small w3-text-grey" style={{ minHeight: '1.4em', marginBottom: '8px' }}>{help || ''}</div>
                  </div>
                )))}
              </div>
            </div>
          )}
          <button className="w3-button w3-green w3-round w3-margin-bottom" onClick={saveProfile}>
            <i className="fa fa-check w3-margin-right"></i>{playerProfile.id ? 'Save Changes' : 'Create Player'}
          </button>
        </div>
      )}
      {!editingProfile && (
        <div className="w3-container w3-padding">
          <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
            {playerProfile.Club && <div><i className="fa fa-building w3-text-theme w3-margin-right"></i>{playerProfile.Club.name}</div>}
            {playerProfile.email && <div><i className="fa fa-envelope w3-text-theme w3-margin-right"></i>{playerProfile.email}</div>}
            {playerProfile.phone && <div><i className="fa fa-phone w3-text-theme w3-margin-right"></i>{playerProfile.phone}</div>}
            {playerProfile.membershipType && <div><i className="fa fa-id-card w3-text-theme w3-margin-right"></i>{playerProfile.membershipType}</div>}
            {playerProfile.playerType && <div><i className="fa fa-users w3-text-theme w3-margin-right"></i>{playerProfile.playerType}</div>}
            {profilePlatformMembershipFields
              .filter(field => canViewPersonalProfileFields || field.personal === false)
              .filter(field => field.isCustom)
              .map(field => {
              const raw = profileDisplayExtraData[field.key];
              if (raw === undefined || raw === null || raw === '') return null;
              const value = field.type === 'boolean' ? (raw ? 'Yes' : 'No') : String(raw);
              return (
                <div key={`profile-membership-view-${field.key}`}>
                  <i className="fa fa-list-alt w3-text-theme w3-margin-right"></i>{field.label}: {value}
                </div>
              );
            })}
            {isAdmin && playerProfile.id && (
              <div>
                <span className={`w3-tag w3-round ${playerProfile.isFinancial !== false ? 'w3-green' : 'w3-red'}`}
                  style={{ fontSize: 'var(--gs-text-sm)', cursor: 'pointer' }}
                  title="Click to toggle financial status"
                  onClick={() => {
                    const newVal = playerProfile.isFinancial === false;
                    fetch(`${API}/api/users/${playerProfile.id}`, {
                      method: 'PUT', headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ isFinancial: newVal }),
                    }).then(r => { if (!r.ok) throw new Error(); return r.json(); })
                      .then(() => {
                        setPlayerProfile(prev => ({ ...prev, isFinancial: newVal }));
                        showToast(newVal ? 'Marked as financial member' : 'Marked as non-financial');
                      })
                      .catch(() => showToast('Failed to update', 'red'));
                  }}>
                  <i className={`fa ${playerProfile.isFinancial !== false ? 'fa-check' : 'fa-times'} w3-margin-right`}></i>
                  {playerProfile.isFinancial !== false ? 'Financial' : 'Non-Financial'}
                </span>
              </div>
            )}
            {!playerProfile.email && !playerProfile.Club && <div className="w3-text-grey"><em>No contact details — click Edit to add</em></div>}
          </div>
        </div>
      )}
    </div>
    {/* Career Overview */}
    {playerProfile.id && (<>
      <div className="w3-card w3-round w3-white w3-margin-top">
        <div className="w3-container w3-theme-l4 w3-round-top" style={{ padding: '12px 16px' }}>
          <strong><i className="fa fa-chart-bar w3-margin-right"></i>Career Overview</strong>
        </div>
        <div className="w3-container w3-padding">
          <div className="stat-grid">
            <div className="stat-box"><div className="stat-number w3-text-theme">{playerProfile.totals.played}</div><div className="stat-label">Played</div></div>
            <div className="stat-box"><div className="stat-number w3-text-green">{playerProfile.totals.wins}</div><div className="stat-label">Wins</div></div>
            <div className="stat-box"><div className="stat-number w3-text-red">{playerProfile.totals.losses}</div><div className="stat-label">Losses</div></div>
            <div className="stat-box"><div className="stat-number w3-text-amber">{playerProfile.totals.draws}</div><div className="stat-label">Draws</div></div>
            <div className="stat-box"><div className="stat-number w3-text-theme">{playerProfile.totals.pointsFor}</div><div className="stat-label">Points For</div></div>
            <div className="stat-box"><div className="stat-number" style={{ color: '#888' }}>{playerProfile.totals.pointsAgainst}</div><div className="stat-label">Points Against</div></div>
            <div className="stat-box">
              <div className={`stat-number ${(playerProfile.totals.pointsFor - playerProfile.totals.pointsAgainst) >= 0 ? 'w3-text-green' : 'w3-text-red'}`}>
                {(playerProfile.totals.pointsFor - playerProfile.totals.pointsAgainst) > 0 ? '+' : ''}{playerProfile.totals.pointsFor - playerProfile.totals.pointsAgainst}
              </div><div className="stat-label">Diff</div>
            </div>
            <div className="stat-box">
              <div className="stat-number w3-text-theme">{playerProfile.totals.played > 0 ? Math.round((playerProfile.totals.wins / playerProfile.totals.played) * 100) : 0}%</div>
              <div className="stat-label">Win Rate</div>
            </div>
            {playerProfile.rating != null && (
              <div className="stat-box" style={{ borderLeft: '3px solid #4CAF50' }}>
                <div className="stat-number" style={{ color: '#4CAF50', fontWeight: 700 }}>{playerProfile.rating}</div>
                <div className="stat-label">Rating</div>
              </div>
            )}
            {playerProfile.compWins > 0 && (
              <div className="stat-box">
                <div className="stat-number" style={{ color: '#FFD700' }}><TrophySvg size={14} color="gold" style={{ marginRight: '4px', verticalAlign: 'middle' }} />{playerProfile.compWins}</div>
                <div className="stat-label">Comp Wins</div>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Club Teams */}
      {playerProfile.clubTeams && playerProfile.clubTeams.length > 0 && (
        <div className="w3-card w3-round w3-white w3-margin-top">
          <div className="w3-container w3-theme-l4 w3-round-top" style={{ padding: '12px 16px' }}>
            <strong><i className="fa fa-shield w3-margin-right"></i>Club Teams ({playerProfile.clubTeams.length})</strong>
          </div>
          <div className="w3-container w3-padding">
            <div className="player-grid">
              {playerProfile.clubTeams.map(ct => (
                <div key={ct.id} className="player-row w3-hover-light-grey" style={{ cursor: 'pointer' }}
                  onClick={() => openTeamProfile(ct.id)}>
                  <div className="player-avatar" style={{ background: '#607d8b' }}><i className="fa fa-shield"></i></div>
                  <div style={{ flex: 1, minWidth: 0 }}><div style={{ fontWeight: 600, fontSize: 'var(--gs-text-sm)' }}>{ct.name}</div></div>
                  <i className="fa fa-chevron-right w3-text-light-grey"></i>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {/* Competition History */}
      <div className="w3-card w3-round w3-white w3-margin-top">
        <div className="w3-container w3-theme-l4 w3-round-top" style={{ padding: '12px 16px' }}>
          <strong><TrophySvg size={14} style={{ marginRight: '8px' }} />Competition History ({playerProfile.competitions.length})</strong>
        </div>
        <div className="w3-container w3-padding">
          {playerProfile.competitions.length === 0 ? (
            <p className="w3-text-grey w3-center"><em>Not yet entered in any competitions</em></p>
          ) : (
            <div className="w3-responsive">
              <table className="w3-table w3-striped w3-bordered w3-small">
                <thead className="w3-theme-d5"><tr><th>Competition</th><th>Format</th><th>Status</th><th>Played</th><th>Won</th><th>Lost</th><th>Drawn</th><th>Points For</th><th>Against</th><th>Difference</th></tr></thead>
                <tbody>
                  {playerProfile.competitions.map(c => {
                    const stats = playerProfile.competitionStats.find(s => s.competitionId === c.id);
                    const fmtLabel = { singles: 'Singles', pairs: 'Pairs', triples: 'Triples', fours: 'Fours', '2-4-2-pairs': '2-4-2', custom: 'Custom' }[c.format] || c.format;
                    return (
                      <tr key={c.id} className="w3-hover-light-grey" style={{ cursor: 'pointer' }} onClick={() => openComp(c)}>
                        <td><strong>{c.name}</strong></td>
                        <td><span className="w3-tag w3-round w3-theme-l3 w3-small">{fmtLabel}</span></td>
                        <td><span className={`w3-tag w3-round w3-small ${c.status === 'in-progress' ? 'w3-green' : c.status === 'completed' ? 'w3-blue' : 'w3-grey'}`}>{c.status || 'draft'}</span></td>
                        {stats ? (<><td>{stats.played}</td><td className="w3-text-green">{stats.wins}</td><td className="w3-text-red">{stats.losses}</td><td>{stats.draws}</td><td>{stats.pointsFor}</td><td>{stats.pointsAgainst}</td>
                          <td className={stats.pointsFor - stats.pointsAgainst >= 0 ? 'w3-text-green' : 'w3-text-red'}>{stats.pointsFor - stats.pointsAgainst > 0 ? '+' : ''}{stats.pointsFor - stats.pointsAgainst}</td>
                        </>) : (<td colSpan="7" className="w3-text-grey"><em>No matches yet</em></td>)}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      {currentUser && playerProfile && playerProfile.id === currentUser.id && (
        <ClubEmailsCard showToast={showToast} profileEmail={playerProfile.email} />
      )}
      {currentUser && playerProfile && playerProfile.id === currentUser.id && (memberQrData || playerProfile.membershipNumber) && (
        <div className="w3-card w3-round w3-white w3-margin-top">
          <div className="w3-container w3-theme-l4 w3-round-top" style={{ padding: '12px 16px' }}>
            <strong><i className="fa fa-id-card w3-margin-right"></i>Membership Details</strong>
          </div>
          <div className="w3-container w3-padding" style={{ display: 'flex', alignItems: 'center', gap: '18px', flexWrap: 'wrap' }}>
            {(memberQrData && memberQrData.qrCode) ? (
              <img src={memberQrData.qrCode} alt="Membership QR" style={{ width: 120, height: 120, border: '1px solid #ddd', borderRadius: 8, background: '#fff' }} />
            ) : null}
            <div style={{ flex: 1, minWidth: '180px' }}>
              <div className="w3-small w3-text-grey">Membership number</div>
              <div style={{ fontSize: '1.7em', fontWeight: 700, letterSpacing: '0.08em' }}>{memberQrData?.membershipNumber || playerProfile.membershipNumber}</div>
              {memberQrData?.url && (
                <div style={{ marginTop: '8px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <button className="w3-button w3-tiny w3-theme-l4 w3-round" onClick={() => navigator.clipboard.writeText(memberQrData.url).catch(() => {})}>
                    <i className="fa fa-copy w3-margin-right"></i>Copy Link
                  </button>
                  <a className="w3-button w3-tiny w3-theme w3-round" href={memberQrData.url} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
                    <i className="fa fa-external-link w3-margin-right"></i>Open Link
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Reset Password */}
      {isAdmin && playerProfile.id && <div className="w3-card w3-round w3-white w3-margin-top">
        <div className="w3-container w3-theme-l4 w3-round-top" style={{ padding: '12px 16px' }}>
          <strong><i className="fa fa-key w3-margin-right"></i>Reset Password</strong>
        </div>
        <div className="w3-container w3-padding">
          {!resetPwOpen ? (
            <>
              <p className="w3-small w3-text-grey">Set a new password for this player if they've forgotten theirs.</p>
              <button className="w3-button w3-amber w3-round" onClick={() => { setResetPwOpen(true); setResetPwValue(''); }}>
                <i className="fa fa-key w3-margin-right"></i>Reset Password
              </button>
            </>
          ) : (
            <div style={{ maxWidth: '360px' }}>
              <label className="w3-small w3-text-grey">New Password (at least 8 characters)</label>
              <input className="w3-input w3-border w3-round w3-margin-bottom" type="password"
                placeholder="Enter new password" value={resetPwValue}
                onChange={e => setResetPwValue(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && resetPlayerPassword()} />
              <div style={{ display: 'flex', gap: '8px' }}>
                <button className="w3-button w3-green w3-round" onClick={resetPlayerPassword}
                  disabled={resetPwValue.length < 8}>
                  <i className="fa fa-check w3-margin-right"></i>Confirm
                </button>
                <button className="w3-button w3-light-grey w3-round" onClick={() => { setResetPwOpen(false); setResetPwValue(''); }}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>}
      {isAdmin && playerProfile.id && (
        <MemberRecordCard userId={playerProfile.id} showToast={showToast} />
      )}
      {/* Danger Zone */}
      {isAdmin && <div className="w3-card w3-round w3-white w3-margin-top w3-margin-bottom">
        <div className="w3-container w3-pale-red w3-round-top" style={{ padding: '12px 16px' }}>
          <strong><i className="fa fa-exclamation-triangle w3-margin-right"></i>Danger Zone</strong>
        </div>
        <div className="w3-container w3-padding">
          <p className="w3-small w3-text-grey">Permanently delete this player and remove them from all competitions and teams. This action cannot be undone.</p>
          <button className="w3-button w3-red w3-round" onClick={deletePlayer}><i className="fa fa-trash w3-margin-right"></i>Delete Player</button>
        </div>
      </div>}
    </>)}
  </>);
}

export default PlayerProfileContent;
